import { useState } from 'react';
import { useRSSArticles } from '@/hooks/useRSS';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Loader2, 
  ExternalLink, 
  Calendar,
  User,
  Sparkles,
  ChevronLeft,
  Newspaper
} from 'lucide-react';
import { cn, formatDate, formatRelativeTime } from '@/utils';
import type { RSSArticle } from '@/types/rss';

function ArticleCard({ article, onClick }: { article: RSSArticle; onClick: () => void }) {
  return (
    <Card 
      className="cursor-pointer hover:border-primary transition-colors"
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex gap-4">
          {/* Image */}
          {article.image_url && (
            <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-muted">
              <img
                src={article.image_url}
                alt={article.title}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          )}
          
          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-semibold line-clamp-2">
                {article.translated_title || article.title}
              </h3>
              {article.ai_urgency_level === 'breaking' && (
                <Badge className="bg-red-500 text-white flex-shrink-0">突发</Badge>
              )}
            </div>
            
            {/* Summary */}
            <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
              {article.translated_summary || article.summary}
            </p>
            
            {/* Meta */}
            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
              {article.source_name && (
                <span className="flex items-center gap-1">
                  <Newspaper className="w-3 h-3" />
                  {article.source_name}
                </span>
              )}
              {article.author && (
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {article.author}
                </span>
              )}
              {article.published_at && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {formatRelativeTime(article.published_at)}
                </span>
              )}
            </div>
            
            {/* Tags */}
            <div className="flex flex-wrap gap-1 mt-2">
              {article.ai_category && (
                <Badge variant="secondary" className="text-xs">
                  {article.ai_category}
                </Badge>
              )}
              {article.ai_topics?.slice(0, 3).map((topic) => (
                <Badge key={topic} variant="outline" className="text-xs">
                  {topic}
                </Badge>
              ))}
              {article.ai_sentiment && (
                <Badge 
                  variant="outline" 
                  className={cn(
                    'text-xs',
                    article.ai_sentiment === 'positive' && 'border-green-500 text-green-600',
                    article.ai_sentiment === 'negative' && 'border-red-500 text-red-600',
                  )}
                >
                  {article.ai_sentiment === 'positive' ? '积极' : 
                   article.ai_sentiment === 'negative' ? '消极' : '中性'}
                </Badge>
              )}
              {article.ai_importance_score !== undefined && article.ai_importance_score > 0.7 && (
                <Badge className="bg-yellow-500 text-white text-xs">
                  <Sparkles className="w-3 h-3 mr-1" />
                  重要
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ArticleDetail({ article, onBack }: { article: RSSArticle; onBack: () => void }) {
  return (
    <div>
      <Button variant="ghost" onClick={onBack} className="mb-4">
        <ChevronLeft className="w-4 h-4 mr-2" />
        返回列表
      </Button>
      
      <Card>
        <CardContent className="p-6">
          {/* Header */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              {article.ai_category && (
                <Badge>{article.ai_category}</Badge>
              )}
              {article.ai_urgency_level && article.ai_urgency_level !== 'normal' && (
                <Badge className={cn(
                  article.ai_urgency_level === 'breaking' && 'bg-red-500',
                  article.ai_urgency_level === 'urgent' && 'bg-orange-500',
                )}>
                  {article.ai_urgency_level === 'breaking' ? '突发' : '重要'}
                </Badge>
              )}
            </div>
            
            <h1 className="text-2xl font-bold">
              {article.translated_title || article.title}
            </h1>
            
            <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
              {article.source_name && (
                <span className="flex items-center gap-1">
                  <Newspaper className="w-4 h-4" />
                  {article.source_name}
                </span>
              )}
              {article.author && (
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  {article.author}
                </span>
              )}
              {article.published_at && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatDate(article.published_at)}
                </span>
              )}
            </div>
          </div>
          
          {/* Image */}
          {article.image_url && (
            <div className="mb-6 rounded-lg overflow-hidden">
              <img
                src={article.image_url}
                alt={article.title}
                className="w-full max-h-[400px] object-cover"
              />
            </div>
          )}
          
          {/* Content */}
          <div className="prose prose-sm max-w-none">
            <p className="text-lg leading-relaxed">
              {article.translated_content || article.translated_summary || article.summary || article.content}
            </p>
          </div>
          
          {/* AI Analysis */}
          {(article.ai_topics?.length || article.ai_entities?.length) && (
            <div className="mt-6 pt-6 border-t">
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                AI 分析
              </h3>
              
              {article.ai_topics && article.ai_topics.length > 0 && (
                <div className="mb-3">
                  <span className="text-sm text-muted-foreground">话题:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {article.ai_topics.map((topic) => (
                      <Badge key={topic} variant="secondary">
                        {topic}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {article.ai_entities && article.ai_entities.length > 0 && (
                <div className="mb-3">
                  <span className="text-sm text-muted-foreground">实体:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {article.ai_entities.map((entity) => (
                      <Badge key={entity} variant="outline">
                        {entity}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {article.ai_sentiment && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">情感:</span>
                  <Badge 
                    variant="outline"
                    className={cn(
                      article.ai_sentiment === 'positive' && 'border-green-500 text-green-600',
                      article.ai_sentiment === 'negative' && 'border-red-500 text-red-600',
                    )}
                  >
                    {article.ai_sentiment === 'positive' ? '积极' : 
                     article.ai_sentiment === 'negative' ? '消极' : '中性'}
                  </Badge>
                </div>
              )}
            </div>
          )}
          
          {/* Actions */}
          <div className="mt-6 pt-6 border-t flex items-center gap-3">
            <Button asChild>
              <a href={article.link} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-4 h-4 mr-2" />
                阅读原文
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function RSSArticlesPage() {
  const [selectedArticle, setSelectedArticle] = useState<RSSArticle | null>(null);
  const [category, setCategory] = useState('all');
  
  const { data, isLoading } = useRSSArticles({
    category: category === 'all' ? undefined : category,
    limit: 50,
  });
  
  if (selectedArticle) {
    return (
      <ArticleDetail 
        article={selectedArticle} 
        onBack={() => setSelectedArticle(null)} 
      />
    );
  }
  
  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">RSS 文章</h2>
        
        <Tabs value={category} onValueChange={setCategory}>
          <TabsList>
            <TabsTrigger value="all">全部</TabsTrigger>
            <TabsTrigger value="news">新闻</TabsTrigger>
            <TabsTrigger value="analysis">分析</TabsTrigger>
            <TabsTrigger value="opinion">观点</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : data?.articles && data.articles.length > 0 ? (
        <div className="space-y-4">
          {data.articles.map((article: RSSArticle) => (
            <ArticleCard
              key={article.id}
              article={article}
              onClick={() => setSelectedArticle(article)}
            />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Newspaper className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">暂无文章</p>
            <p className="text-sm text-muted-foreground mt-2">
              添加 RSS 源后，文章将自动同步
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
